import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Heart, Mail, MapPin, Phone, Send } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { usePageContent } from "../hooks/usePageContent";

const GOFUNDME_URL = "https://gofund.me/274183352";

const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold, rootMargin: '50px' });
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);
  return { ref, isVisible };
};

const AnimatedSection = ({ children, className = "", animation = "fly-up", delay = 0 }) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const baseTransform = { 'fly-left': 'translateX(-80px)', 'fly-right': 'translateX(80px)', 'fly-up': 'translateY(60px)', 'fly-down': 'translateY(-60px)', 'zoom': 'scale(0.8)' };
  const style = { opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateX(0) translateY(0) scale(1)' : baseTransform[animation] || 'translateY(60px)', transition: `opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms, transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms` };
  return <div ref={ref} className={className} style={style}>{children}</div>;
};

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const { lang, t } = useLanguage();
  const { get } = usePageContent("contact");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`/api/contact`, formData);
      toast.success(t.contact.success);
      setFormData({ name: "", email: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const title = lang === "es" ? "Contacto | Curious 123" : "Contact Us | Curious 123 Youth Soccer";
  const desc = lang === "es" ? "Contacta a Curious 123 para preguntas, inscripciones o para apoyar nuestra mision." : "Contact Curious 123 for questions, enrollment, or to support our mission in San Juan del Sur, Nicaragua.";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href="https://curious123.org/contact" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content="https://curious123.org/contact" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Curious 123" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
      </Helmet>

      <section className="pt-32 pb-20 bg-gradient-to-b from-[#F0F9FF] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-up" className="text-center max-w-4xl mx-auto">
            <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">{t.contact.badge}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-tight mb-6 font-['Kanit']">{t.contact.title}</h1>
            <p className="text-lg text-gray-700">{t.contact.subtitle}</p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <AnimatedSection animation="fly-left">
              <h2 className="text-3xl font-bold text-black mb-8 font-['Kanit']">{t.contact.getInTouch}</h2>
              <div className="space-y-6">
                {[
                  { icon: Mail, label: t.contact.email, value: get("email", "curious123soccer@gmail.com"), href: `mailto:${get("email", "curious123soccer@gmail.com")}` },
                  { icon: MapPin, label: t.contact.location, value: get("address", "San Juan del Sur, Nicaragua") },
                  { icon: Phone, label: t.contact.phone, value: get("phone", t.contact.phoneVal) }
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4 p-4 rounded-2xl hover:bg-gray-50 transition-colors">
                    <div className="w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center flex-shrink-0"><item.icon className="w-6 h-6 text-black" /></div>
                    <div>
                      <p className="font-semibold text-black font-['Kanit']">{item.label}</p>
                      {item.href ? <a href={item.href} className="text-gray-600 hover:text-[#87CEEB] transition-colors">{item.value}</a> : <p className="text-gray-600">{item.value}</p>}
                    </div>
                  </div>
                ))}
              </div>
              <Card className="mt-8 bg-[#F0F9FF] border-[#87CEEB] border rounded-2xl">
                <CardContent className="p-6 text-center">
                  <h3 className="font-bold text-xl text-black mb-4 font-['Kanit']">{t.contact.supportTitle}</h3>
                  <p className="text-gray-600 mb-4">{t.contact.supportDesc}</p>
                  <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer">
                    <Button className="donate-btn rounded-full px-8 py-3 font-semibold uppercase tracking-wider"><Heart className="w-4 h-4 mr-2" />{t.contact.donateBtn}</Button>
                  </a>
                </CardContent>
              </Card>
            </AnimatedSection>
            <AnimatedSection animation="fly-right" delay={200}>
              <Card className="bg-white border-gray-200 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-black mb-2 font-['Kanit']">{t.contact.formTitle}</h2>
                  <p className="text-gray-600 mb-6">{t.contact.formDesc}</p>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.name}</label>
                      <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder={t.contact.namePlaceholder} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#87CEEB] focus:border-transparent transition-all" required data-testid="contact-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.emailLabel}</label>
                      <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder={t.contact.emailPlaceholder} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#87CEEB] focus:border-transparent transition-all" required data-testid="contact-email" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t.contact.message}</label>
                      <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder={t.contact.messagePlaceholder} rows={5} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#87CEEB] focus:border-transparent transition-all resize-none" required data-testid="contact-message" />
                    </div>
                    <Button type="submit" disabled={sending} className="donate-btn w-full rounded-full py-4 text-lg font-bold uppercase tracking-wider" data-testid="contact-submit">
                      {sending ? t.contact.sending : <><Send className="w-5 h-5 mr-2" />{t.contact.sendMessage}</>}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-up" className="text-center mb-8">
            <h2 className="text-2xl font-bold text-black font-['Kanit']">{t.contact.findUs}</h2>
          </AnimatedSection>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe title="Map" src={get("map_embed", "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31234.22!2d-85.87!3d11.25!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8f75c4a5a1c3c3c3%3A0x0!2sSan%20Juan%20del%20Sur%2C%20Nicaragua!5e0!3m2!1sen!2s!4v1")} width="100%" height="400" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
